import { Badge } from "design-system/atoms/Badge"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import DateDisplay from "design-system/molecules/DateDisplay"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { formatNumber, formatPercent } from "lopermut"
import { ChevronDown, ChevronRight } from "lucide-react"
import { Fragment, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { BackofficeWhatsappRouteResponseDataSchema } from "schema"
import { z } from "zod"

const COLUMNS = 9

type Route = z.infer<typeof BackofficeWhatsappRouteResponseDataSchema>

type Props = {
  routes: Route[] | undefined
  isPending: boolean
}

export function RoutesTable({ routes, isPending }: Props) {
  const { t } = useTranslation()
  const [expandedSenders, setExpandedSenders] = useState<Set<string>>(new Set())

  const senders = useMemo(() => groupBySender(routes ?? []), [routes])

  function toggleSender(senderCallingCode: string) {
    setExpandedSenders((previous) => {
      const next = new Set(previous)

      if (next.has(senderCallingCode)) next.delete(senderCallingCode)
      else next.add(senderCallingCode)

      return next
    })
  }

  function renderRows() {
    if (isPending || !routes) {
      return <TableSkeleton colSpans={Array(COLUMNS).fill(1)} />
    }

    if (senders.length === 0) {
      return (
        <TableEmptySlate colSpan={COLUMNS} message={t("backoffice.whatsappRoutes.routes.empty")} />
      )
    }

    return senders.map((sender) => {
      const isExpanded = expandedSenders.has(sender.senderCallingCode)

      return (
        <Fragment key={sender.senderCallingCode}>
          <TableRow
            className="cursor-pointer"
            onClick={() => toggleSender(sender.senderCallingCode)}
          >
            <TableCell className="font-medium">
              <span className="flex items-center gap-1.5">
                {isExpanded ? (
                  <ChevronDown className="size-4" />
                ) : (
                  <ChevronRight className="size-4" />
                )}
                <span className="font-mono">{`+${sender.senderCallingCode}`}</span>
                <span className="text-muted-foreground">
                  {t("backoffice.whatsappRoutes.routes.routesCount", {
                    count: sender.routes.length,
                  })}
                </span>
              </span>
            </TableCell>
            <TableCell>
              <SenderStatusBadge blockedRoutesCount={sender.blockedRoutesCount} />
            </TableCell>
            <TableCell align="right">
              {formatNumber({ amount: sender.deliveredCount, fix: 0 })}
            </TableCell>
            <TableCell align="right">
              {formatNumber({ amount: sender.blockedCount, fix: 0 })}
            </TableCell>
            <TableCell colSpan={3} />
            <TableCell>
              {sender.lastDeliveredAt ? <DateDisplay date={sender.lastDeliveredAt} /> : "-"}
            </TableCell>
            <TableCell>
              {sender.lastBlockedAt ? <DateDisplay date={sender.lastBlockedAt} /> : "-"}
            </TableCell>
          </TableRow>
          {isExpanded &&
            sender.routes.map((route) => <RouteRow key={routeKey(route)} route={route} />)}
        </Fragment>
      )
    })
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("backoffice.whatsappRoutes.routes.table.route")}</TableHead>
          <TableHead>{t("backoffice.whatsappRoutes.routes.table.status")}</TableHead>
          <TableHead align="right">
            {t("backoffice.whatsappRoutes.routes.table.delivered")}
          </TableHead>
          <TableHead align="right">{t("backoffice.whatsappRoutes.routes.table.blocked")}</TableHead>
          <TableHead align="right">
            {t("backoffice.whatsappRoutes.routes.table.blockedProbability")}
          </TableHead>
          <TableHead align="right">
            <Tooltip>
              <TooltipTrigger slick>
                {t("backoffice.whatsappRoutes.routes.table.evidence")}
              </TooltipTrigger>
              <TooltipContent className="text-left">
                {t("backoffice.whatsappRoutes.routes.table.evidenceTooltip")}
              </TooltipContent>
            </Tooltip>
          </TableHead>
          <TableHead align="right">{t("backoffice.whatsappRoutes.routes.table.numbers")}</TableHead>
          <TableHead>{t("backoffice.whatsappRoutes.routes.table.lastDeliveredAt")}</TableHead>
          <TableHead>{t("backoffice.whatsappRoutes.routes.table.lastBlockedAt")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>{renderRows()}</TableBody>
    </Table>
  )
}

function RouteRow({ route }: { route: Route }) {
  return (
    <TableRow className="bg-muted/40">
      <TableCell className="pl-11 font-mono">
        {`+${route.senderCallingCode} → +${route.recipientCallingCode}`}
      </TableCell>
      <TableCell>
        <RouteStatusBadge route={route} />
      </TableCell>
      <TableCell align="right">{formatNumber({ amount: route.deliveredCount, fix: 0 })}</TableCell>
      <TableCell align="right">{formatNumber({ amount: route.blockedCount, fix: 0 })}</TableCell>
      <TableCell align="right">
        {formatPercent({ amount: route.blockedProbability * 100, fix: 0 })}
      </TableCell>
      <TableCell align="right">{formatNumber({ amount: route.evidence, fix: 1 })}</TableCell>
      <TableCell align="right">{route.numbersCount}</TableCell>
      <TableCell>
        {route.lastDeliveredAt ? <DateDisplay date={route.lastDeliveredAt} /> : "-"}
      </TableCell>
      <TableCell>
        {route.lastBlockedAt ? <DateDisplay date={route.lastBlockedAt} /> : "-"}
      </TableCell>
    </TableRow>
  )
}

function RouteStatusBadge({ route }: { route: Route }) {
  const { t } = useTranslation()

  if (!route.hasEnoughEvidence) {
    return <Badge variant="cached">{t("backoffice.whatsappRoutes.routes.statuses.unknown")}</Badge>
  }

  if (route.isBlocked) {
    return (
      <Badge variant="negative">{t("backoffice.whatsappRoutes.routes.statuses.blocked")}</Badge>
    )
  }

  return <Badge variant="success">{t("backoffice.whatsappRoutes.routes.statuses.open")}</Badge>
}

function SenderStatusBadge({ blockedRoutesCount }: { blockedRoutesCount: number }) {
  const { t } = useTranslation()

  if (blockedRoutesCount === 0) {
    return <Badge variant="success">{t("backoffice.whatsappRoutes.routes.statuses.open")}</Badge>
  }

  return (
    <Badge variant="negative">
      {t("backoffice.whatsappRoutes.routes.statuses.blockedRoutes", { count: blockedRoutesCount })}
    </Badge>
  )
}

function groupBySender(routes: Route[]) {
  const routesBySender = Map.groupBy(routes, (route) => route.senderCallingCode)

  return Array.from(routesBySender, ([senderCallingCode, senderRoutes]) => ({
    senderCallingCode,
    routes: senderRoutes.toSorted(compareRoutes),
    blockedRoutesCount: senderRoutes.filter(({ isBlocked }) => isBlocked).length,
    deliveredCount: senderRoutes.reduce((total, route) => total + route.deliveredCount, 0),
    blockedCount: senderRoutes.reduce((total, route) => total + route.blockedCount, 0),
    evidence: senderRoutes.reduce((total, route) => total + route.evidence, 0),
    lastDeliveredAt: latestDate(senderRoutes.map(({ lastDeliveredAt }) => lastDeliveredAt)),
    lastBlockedAt: latestDate(senderRoutes.map(({ lastBlockedAt }) => lastBlockedAt)),
  })).toSorted(
    (a, b) =>
      Number(b.blockedRoutesCount > 0) - Number(a.blockedRoutesCount > 0) ||
      b.evidence - a.evidence,
  )
}

function compareRoutes(a: Route, b: Route) {
  return Number(b.isBlocked) - Number(a.isBlocked) || b.evidence - a.evidence
}

function routeKey(route: Route) {
  return `${route.senderCallingCode}-${route.recipientCallingCode}`
}

function latestDate(dates: (string | null)[]) {
  return dates.reduce<string | null>(
    (latest, date) => (date && (!latest || date > latest) ? date : latest),
    null,
  )
}
